import { Panel } from "../Panel"
import type { DynamicPanelSpec } from "../chat-types"

const PALETTE = ["#22d3ee", "#f97316", "#a78bfa", "#34d399", "#f43f5e"]

function LineChart({ spec }: { spec: DynamicPanelSpec }) {
  const all = spec.series.flatMap((s) => s.values)
  if (all.length === 0) return <Empty />
  const min = Math.min(...all)
  const max = Math.max(...all)
  const range = max - min || 1
  const width = 300
  const height = 140
  const pad = 8

  const firstSeries = spec.series[0]
  const xStep = (width - pad * 2) / Math.max(1, (firstSeries?.values.length ?? 1) - 1)
  const y = (v: number) => height - pad - ((v - min) / range) * (height - pad * 2)

  return (
    <svg viewBox={`0 0 ${width} ${height}`} className="w-full h-full">
      {spec.series.map((s, idx) => {
        const color = s.color ?? PALETTE[idx % PALETTE.length]
        const d = s.values.map((v, i) => `${i === 0 ? "M" : "L"} ${pad + i * xStep} ${y(v)}`).join(" ")
        return (
          <path
            key={s.id}
            d={d}
            fill="none"
            stroke={color}
            strokeWidth={1.5}
            strokeDasharray={s.dashed ? "4 3" : undefined}
          />
        )
      })}
      {spec.annotations?.map((a, i) =>
        a.type === "vline" ? (
          <g key={i}>
            <line
              x1={pad + a.x * xStep}
              x2={pad + a.x * xStep}
              y1={pad}
              y2={height - pad}
              stroke="var(--text-dim)"
              strokeDasharray="2 2"
            />
            <text
              x={pad + a.x * xStep + 2}
              y={pad + 10}
              fill="var(--text-dim)"
              fontSize="9"
            >
              {a.label}
            </text>
          </g>
        ) : null,
      )}
    </svg>
  )
}

function LineBandChart({ spec }: { spec: DynamicPanelSpec }) {
  const mid = spec.series[0]
  const low = spec.series.find((s) => s.id.endsWith("-low"))
  const high = spec.series.find((s) => s.id.endsWith("-high"))
  if (!mid) return <Empty />
  const all = spec.series.flatMap((s) => s.values)
  const min = Math.min(...all)
  const max = Math.max(...all)
  const range = max - min || 1
  const width = 300
  const height = 140
  const pad = 8
  const xStep = (width - pad * 2) / Math.max(1, mid.values.length - 1)
  const y = (v: number) => height - pad - ((v - min) / range) * (height - pad * 2)
  const color = mid.color ?? PALETTE[0]

  let bandPath = ""
  if (low && high) {
    const up = high.values.map((v, i) => `${pad + i * xStep},${y(v)}`).join(" ")
    const dn = low.values.map((v, i) => `${pad + i * xStep},${y(v)}`).reverse().join(" ")
    bandPath = `${up} ${dn}`
  }
  const line = mid.values.map((v, i) => `${i === 0 ? "M" : "L"} ${pad + i * xStep} ${y(v)}`).join(" ")

  return (
    <svg viewBox={`0 0 ${width} ${height}`} className="w-full h-full">
      {bandPath && <polygon points={bandPath} fill={color} opacity={0.15} />}
      <path d={line} fill="none" stroke={color} strokeWidth={1.5} />
    </svg>
  )
}

function BarChart({ spec }: { spec: DynamicPanelSpec }) {
  const firstSeries = spec.series[0]
  if (!firstSeries) return <Empty />
  const vals = firstSeries.values
  const max = Math.max(...vals, 1)
  return (
    <div className="flex items-end gap-1 h-full px-2 py-2">
      {vals.map((v, i) => (
        <div
          key={i}
          className="flex-1 bg-[color:var(--accent)]/60 rounded-sm"
          style={{ height: `${(v / max) * 100}%` }}
          title={String(v)}
        />
      ))}
    </div>
  )
}

function NumberTile({ spec }: { spec: DynamicPanelSpec }) {
  const firstSeries = spec.series[0]
  const value = firstSeries?.values.slice(-1)[0] ?? 0
  const label = firstSeries?.label ?? ""
  return (
    <div className="flex flex-col items-center justify-center h-full">
      <div className="text-3xl font-semibold text-[color:var(--text)] tabular-nums">
        {typeof value === "number" ? value.toLocaleString() : value}
      </div>
      <div className="text-[10px] text-[color:var(--text-dim)] uppercase tracking-wider mt-1">
        {label}
      </div>
    </div>
  )
}

function TableView({ spec }: { spec: DynamicPanelSpec }) {
  return (
    <table className="w-full text-[11px]">
      <thead>
        <tr>
          <th className="text-left px-2 py-1 text-[color:var(--text-dim)] font-normal">series</th>
          <th className="text-right px-2 py-1 text-[color:var(--text-dim)] font-normal">latest</th>
        </tr>
      </thead>
      <tbody>
        {spec.series.map((s) => (
          <tr key={s.id} className="border-t border-[color:var(--border)]">
            <td className="px-2 py-1">{s.label}</td>
            <td className="px-2 py-1 text-right tabular-nums">{s.values.slice(-1)[0]}</td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

function Empty() {
  return <div className="h-full flex items-center justify-center text-[color:var(--text-dim)] text-[11px]">no data</div>
}

export function DynamicPanel({ spec }: { spec: DynamicPanelSpec }) {
  let body: React.ReactNode
  switch (spec.chart_type) {
    case "line":
      body = <LineChart spec={spec} />
      break
    case "line_band":
      body = <LineBandChart spec={spec} />
      break
    case "bar":
      body = <BarChart spec={spec} />
      break
    case "number_tile":
      body = <NumberTile spec={spec} />
      break
    case "table":
      body = <TableView spec={spec} />
      break
    default:
      body = <Empty />
  }
  return (
    <Panel title={spec.title} footer={spec.footer} helpText="AI-generated panel">
      <div className="h-full">{body}</div>
    </Panel>
  )
}
